/**
 * About WarCarp — /(app)/settings/about
 *
 * Static informational page: blurb, version, and two links.
 */

import { COMPACT_HEADER_STYLE } from '../../../lib/gameChrome'
import React from 'react'
import { View, Text, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useOrientation } from '../../../hooks/useOrientation'
import { goBackOr } from '../../../lib/goBack'
import { APP_VERSION } from '../../../lib/appVersion'


export default function AboutScreen() {
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const contentWidth = isLandscape ? Math.min(560, winWidth * 0.6) : undefined

  return (
    <SafeAreaView className="flex-1 bg-canvas">
      {/* Header */}
      <View className="flex-row items-center px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
        <TouchableOpacity
          onPress={() => goBackOr(router, '/(app)/settings')}
          accessibilityLabel="Back to settings"
          accessibilityRole="button"
          className="mr-3 p-1"
        >
          <Text className="text-blood text-base font-typed">← Back</Text>
        </TouchableOpacity>
        <Text className="text-ink font-stencil text-xl flex-1 uppercase">About WarCarp</Text>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 16,
          gap: 16,
          alignItems: isLandscape ? 'center' : undefined,
        }}
      >
        <View style={isLandscape ? { width: contentWidth, gap: 16 } : { width: '100%', gap: 16 }}>
        {/* Blurb */}
        <View className="bg-paper border-2 border-olive rounded-xl p-4 gap-3">
          <Text className="font-stencil text-ink uppercase text-sm tracking-widest">
            The Game
          </Text>
          <Text className="font-typed text-ink text-sm leading-6">
            WarCarp is a love letter to{' '}
            <Text className="font-stencil">warfish.net</Text> (2005–2022) — a
            browser-based, asynchronous game of global conquest.
          </Text>
          <Text className="font-typed text-ink text-sm leading-6">
            WarCarp keeps the bones that made Warfish work—the three-phase
            turn (reinforce → attack → fortify) and long-running asynchronous
            games—and rebuilds them with a mobile-first design.
          </Text>
        </View>

        {/* Links */}
        <View className="bg-paper border-2 border-olive rounded-xl overflow-hidden">
          <Text className="text-ink-soft text-xs font-stencil uppercase tracking-widest px-4 pt-4 pb-2">
            Links
          </Text>

          <TouchableOpacity
            onPress={() => router.push('/(app)/settings/rules')}
            accessibilityLabel="How to play"
            accessibilityRole="button"
            className="flex-row items-center justify-between px-4 py-3 border-t border-olive"
            style={{ minHeight: 48 }}
          >
            <Text className="text-ink font-typed">How to Play</Text>
            <Text className="text-blood font-typed">›</Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => router.push('/(app)/settings/contact')}
            accessibilityLabel="Contact"
            accessibilityRole="button"
            className="flex-row items-center justify-between px-4 py-3 border-t border-olive"
            style={{ minHeight: 48 }}
          >
            <Text className="text-ink font-typed">Contact</Text>
            <Text className="text-blood font-typed">›</Text>
          </TouchableOpacity>
        </View>

        {/* Version */}
        <View className="bg-paper border-2 border-olive rounded-xl overflow-hidden">
          <Text className="text-ink-soft text-xs font-stencil uppercase tracking-widest px-4 pt-4 pb-2">
            App Info
          </Text>
          <View className="flex-row items-center justify-between px-4 py-3 border-t border-olive">
            <Text className="text-ink font-typed">Version</Text>
            <Text className="text-ink-soft font-typed">{APP_VERSION}</Text>
          </View>
        </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}
